import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './BehaviorJournal.module.css'
import Icon from '@/components/ui/Icon'

interface JournalEntry {
  id: string; date: string; description: string; trigger: string; intensity: number; whatHelped: string
}

const TRIGGERS = ['Before meal', 'After screen time', 'Tired/sleepy', 'Transition/change', 'Overstimulated', 'Hungry', 'No apparent trigger', 'Other']
const INTENSITY_EMOJI = ['', '', '', '', '']
const STORAGE_KEY = 'mellomap-behavior-journal'

function loadEntries(): JournalEntry[] {
  const d = localStorage.getItem(STORAGE_KEY)
  return d ? JSON.parse(d) : []
}
function saveEntries(entries: JournalEntry[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)) }

function getInsights(entries: JournalEntry[]) {
  if (entries.length < 3) return null
  const triggerCounts: Record<string, number> = {}
  const timeCounts: Record<string, number> = { Morning: 0, Afternoon: 0, Evening: 0 }
  let totalIntensity = 0
  entries.forEach(e => {
    triggerCounts[e.trigger] = (triggerCounts[e.trigger] || 0) + 1
    const h = new Date(e.date).getHours()
    if (h < 12) timeCounts.Morning++; else if (h < 17) timeCounts.Afternoon++; else timeCounts.Evening++
    totalIntensity += e.intensity
  })
  const topTrigger = Object.entries(triggerCounts).sort((a, b) => b[1] - a[1])[0]
  const topTime = Object.entries(timeCounts).sort((a, b) => b[1] - a[1])[0]
  return { topTrigger: topTrigger[0], topTime: topTime[0], avgIntensity: (totalIntensity / entries.length).toFixed(1), count: entries.length }
}

export default function BehaviorJournal() {
  const navigate = useNavigate()
  const [entries, setEntries] = useState<JournalEntry[]>(loadEntries)
  const [showForm, setShowForm] = useState(false)
  const [desc, setDesc] = useState('')
  const [trigger, setTrigger] = useState(TRIGGERS[0])
  const [intensity, setIntensity] = useState(3)
  const [whatHelped, setWhatHelped] = useState('')

  const insights = getInsights(entries)

  const handleSave = () => {
    if (!desc.trim()) return
    const entry: JournalEntry = { id: Date.now().toString(), date: new Date().toISOString(), description: desc.trim(), trigger, intensity, whatHelped: whatHelped.trim() }
    const updated = [entry, ...entries]
    setEntries(updated); saveEntries(updated)
    setDesc(''); setTrigger(TRIGGERS[0]); setIntensity(3); setWhatHelped(''); setShowForm(false)
  }

  const deleteEntry = (id: string) => {
    const updated = entries.filter(e => e.id !== id)
    setEntries(updated); saveEntries(updated)
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <h1 className={styles.title}>Behavior Journal</h1>
        <button className={styles.addButton} onClick={() => setShowForm(!showForm)}>{showForm ? '✕' : '+ New'}</button>
      </header>

      {showForm && (
        <div className={styles.form}>
          <label className={styles.label}>What happened?</label>
          <textarea className={styles.textarea} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe what happened..." rows={3} />
          <label className={styles.label}>Trigger</label>
          <select className={styles.select} value={trigger} onChange={e => setTrigger(e.target.value)}>
            {TRIGGERS.map(t =><option key={t} value={t}>{t}</option>)}
          </select>
          <label className={styles.label}>Intensity</label>
          <div className={styles.intensityRow}>
            {INTENSITY_EMOJI.map((emoji, i) => (
              <button key={i} className={`${styles.intensityBtn} ${intensity === i + 1 ? styles.intensityActive : ''}`} onClick={() => setIntensity(i + 1)}>
                <span className={styles.intensityEmoji}>{emoji}</span>
                <span className={styles.intensityNum}>{i + 1}</span>
              </button>
            ))}
          </div>
          <label className={styles.label}>What helped?</label>
          <textarea className={styles.textarea} value={whatHelped} onChange={e => setWhatHelped(e.target.value)} placeholder="What calmed things down?" rows={2} />
          <button className={styles.saveButton} onClick={handleSave} disabled={!desc.trim()}>Save Entry</button>
        </div>
      )}

      {insights && (
        <div className={styles.insights}>
          <h3 className={styles.insightsTitle}>Patterns ({insights.count} entries)</h3>
          <div className={styles.insightCards}>
            <div className={styles.insightCard}><span><Icon name="target" size={22} /></span><span>Top trigger: <strong>{insights.topTrigger}</strong></span></div>
            <div className={styles.insightCard}><span><Icon name="clock" size={22} /></span><span>Most common: <strong>{insights.topTime}</strong></span></div>
            <div className={styles.insightCard}><span><Icon name="bar-chart-3" size={22} /></span><span>Avg intensity: <strong>{insights.avgIntensity}/5</strong></span></div>
          </div>
        </div>
      )}

      <div className={styles.entries}>
        {entries.length === 0 && <p className={styles.empty}>No entries yet. Start logging to discover patterns.</p>}
        {entries.map(e => (
          <div key={e.id} className={styles.entryCard} data-intensity={e.intensity}>
            <div className={styles.entryHeader}>
              <span className={styles.entryDate}>{new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>
              <span className={styles.entryIntensity}>{INTENSITY_EMOJI[e.intensity - 1]}</span>
              <button className={styles.deleteBtn} onClick={() => deleteEntry(e.id)}>✕</button>
            </div>
            <p className={styles.entryDesc}>{e.description}</p>
            <div className={styles.entryMeta}>
              <span className={styles.triggerBadge}>{e.trigger}</span>
              {e.whatHelped && <span className={styles.helpedText}>Helped: {e.whatHelped}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
